import { TradingEntityAssignmentDetail, TradingType, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import { isNil } from 'lodash'
import { createInvalidValueValidationIssue } from 'model'
import { compareInsensitive } from 'utils'
import { v4 } from 'uuid'
import { isNotNil } from 'utils/predicate'
import {
  PendingTradingEntityAssignment,
  PendingTradingEntityAssignmentFields,
  TradingEntityAssignmentSpreadsheetParseResult
} from './parseTradingEntityAssignmentSpreadsheet'

interface FundAssignmentParseResultValidatorContext {
  assignmentDetails: TradingEntityAssignmentDetail[]
  assignmentType: TradingType
}

export interface SpreadsheetParseValidationResult<T> {
  validationIssues: ValidationIssue[]
  item: T & { _id: string }
}

export interface TradingEntityAssignmentValidatedParseResult extends SpreadsheetParseValidationResult<PendingTradingEntityAssignment> {
  validationIssues: ValidationIssue[]
}

export const validateTradingEntityAssignmentParseResult = (context: FundAssignmentParseResultValidatorContext) => (
  result: TradingEntityAssignmentSpreadsheetParseResult
): TradingEntityAssignmentValidatedParseResult[] => {
  const { assignmentDetails, assignmentType } = context

  return result.parsedItems.map<TradingEntityAssignmentValidatedParseResult>(pending => {
    const { buySideEntity, notional, startConsideration } = pending

    const matchingAssignment = isNotNil(buySideEntity)
      ? assignmentDetails.find(a => compareInsensitive(a.tradingEntity.shortName, buySideEntity) === 0)
      : undefined

    return {
      item: {
        ...pending,
        tradingEntityId: matchingAssignment ? matchingAssignment.tradingEntity.id : null,
        selected: matchingAssignment ? true : false,
        _id: v4()
      },
      validationIssues: [
        ...(assignmentType === TradingType.NOTIONAL && isNotNil(startConsideration)
          ? [
              createInvalidValueValidationIssue(
                PendingTradingEntityAssignmentFields.START_CONSIDERATION,
                Locale.translate(Translation.SC_TRADING_ENTITY_ASSIGN_UPLOAD_NO_SC_FOR_NOTIONAL_TRADED)
              )
            ]
          : []),
        ...(assignmentType === TradingType.CASH && isNotNil(notional)
          ? [
              createInvalidValueValidationIssue(
                PendingTradingEntityAssignmentFields.NOTIONAL,
                Locale.translate(Translation.SC_TRADING_ENTITY_ASSIGN_UPLOAD_NO_NOTIONAL_FOR_CASH_TRADED)
              )
            ]
          : []),
        ...(isNil(matchingAssignment)
          ? [
              createInvalidValueValidationIssue(
                PendingTradingEntityAssignmentFields.BUY_SIDE_ENTITY,
                Locale.translate(Translation.SC_TRADING_ENTITY_ASSIGN_UPLOAD_ERROR_ENTITY_NOT_FOUND, { entity: pending.buySideEntity })
              )
            ]
          : [])
      ]
    }
  })
}
